import React, { useState, useEffect } from "react";
import { useEtatGlobal } from "../EtatGlobal.js";
import { authFetch } from '../../HttpClient';

import {
  Building2,
  X,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Loader2,
  ClipboardCheck,
  FileText,
} from "lucide-react";

const AutresPopup = ({ selectedClient, setSelectedClient, fetchTodos }) => {
  const [isLoading] = useState(false);
  const [commentaireInput, setCommentaireInput] = useState("");
  const [isSendingComment, setIsSendingComment] = useState(false);
  const [commentaires, setCommentaires] = useState([]);
  const { utilisateur, patchTodoLocal } = useEtatGlobal();

  useEffect(() => {
    setCommentaires(selectedClient?.commentaires || []);
  }, [selectedClient]);

  if (!selectedClient) return null;

  const handleValidation = async () => {
    // UI instantanée : la tâche passe en "Terminer" et la popup se ferme
    // tout de suite, les appels API se font en arrière-plan
    const todo = selectedClient;
    patchTodoLocal(todo.id, { comptaDext: true, status: "Terminer" });
    setSelectedClient(null);

    try {
      // --- Étape 1 : Mise à jour du todo
      const response = await authFetch(`https://backend-myalfa.vercel.app/api/todos/${todo.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            comptaDext: true,
            status: "Terminer",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors de la mise à jour du client");
      }

      // --- Étape 2 : Création d’un historique client (SEULEMENT si clientId existe et n'est pas vide)
      if (todo?.clientId && todo.clientId.trim() !== "") {
        try {
          const formattedDate = new Date(
            todo?.createdAt
          ).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });

          await authFetch("https://backend-myalfa.vercel.app/api/historique-client",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                titre: todo?.title,
                description: `Finalisation de la tâche "${todo?.title}" - intégrée le ${formattedDate}`,
                clientId: todo?.clientId,
              }),
            }
          );

          console.log("Historique client créé avec succès !");
        } catch (histError) {
          console.error(
            "Erreur lors de la création de l'historique client :",
            histError
          );
        }
      } else {
        console.log(
          "Historique non créé : clientId est nul ou vide pour cette tâche."
        );
      }

      fetchTodos();
    } catch (error) {
      console.error("Erreur lors de la validation :", error);
      fetchTodos(); // resynchronise l'état réel
    }
  };

  const handleAnnulation = async () => {
    // UI instantanée : même principe que la validation
    const todoId = selectedClient.id;
    patchTodoLocal(todoId, { status: "Annuler" });
    setSelectedClient(null);

    try {
      const response = await authFetch(`https://backend-myalfa.vercel.app/api/todos/${todoId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "Annuler",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors de la mise à jour du client");
      }

      fetchTodos();
    } catch (error) {
      console.error("Erreur lors de la Annulation :", error);
      fetchTodos(); // resynchronise l'état réel
    }
  };

  const handleAddComment = async () => {
    if (!commentaireInput.trim()) return;

    const texte = commentaireInput;
    const commentaireOptimiste = {
      nomComplet: `${utilisateur.lastName} ${utilisateur.name}`,
      commentaire: texte,
      createdAt: new Date().toISOString(),
      _pending: true,
    };

    setCommentaires((prev) => [...prev, commentaireOptimiste]);
    setCommentaireInput("");
    setIsSendingComment(true);

    try {
      const response = await authFetch("https://backend-myalfa.vercel.app/api/commentairesTodo",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nomComplet: `${utilisateur.lastName} ${utilisateur.name}`,
            commentaire: texte,
            todoId: selectedClient.id,
          }),
        }
      );

      if (!response.ok) throw new Error("Erreur lors de l'ajout du commentaire");

      fetchTodos();
    } catch (error) {
      console.error("Erreur en ajoutant le commentaire :", error);
      setCommentaires((prev) => prev.filter((c) => c !== commentaireOptimiste));
      setCommentaireInput(texte);
    } finally {
      setIsSendingComment(false);
    }
  };

  return (
    <div className="overlayAC">
      <div className="inoverlayLead">
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            width: "90%",
            margin: "0 auto",
          }}
        >
          <div className="bg-white border-b-4 border-[#8B1538] px-4 pb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                {/* Logo Cabinet */}
                <img
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6969eee7337849d9e1d7fcb4/2957c578c_LogoAlfa-PNG-SansCartouche.png"
                  alt="Alfa"
                  className="h-16 object-contain"
                />
                <div className="h-12 w-px bg-slate-200" />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Tâches à faire
                    </span>
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900">
                    {selectedClient.nomClient}
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-2 justify-end">
                <button
                  aria-label="Fermer"
                  className="p-2 rounded-md text-gray-500 hover:text-gray-800 transition"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedClient(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="w-full">
            <div className="bg-slate-50 rounded-lg border border-slate-200 p-6 mt-6">
              {/* Header */}
              <div className="flex items-center gap-2 mb-4">
                <ClipboardCheck className="w-5 h-5 text-[#840040]" />
                <p className="text-sm font-medium text-slate-700">
                  Validation de la tâche
                </p>
              </div>

              {/* Contenu principal */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">
                  {selectedClient?.title}
                </h2>

                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <p className="text-xs uppercase tracking-wide text-slate-500">
                      Description
                    </p>
                  </div>

                  <p className="text-sm text-slate-700 leading-relaxed">
                    {selectedClient?.commentaire}
                  </p>
                </div>
              </div>

              {/* Bouton Validation */}
              {selectedClient.status !== "Terminer" && (
                <button
                  onClick={handleValidation}
                  disabled={isLoading}
                  className="w-full mb-3 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Validation en attente...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Valider la tâche
                    </>
                  )}
                </button>
              )}

              {/* Bouton Annulation */}
              {(utilisateur.role === "ADMINISTRATEUR" ||
                utilisateur.role === "EXPERT_COMPTABLE") && (
                <button
                  onClick={handleAnnulation}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Annulation en cours...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Annuler la tâche
                    </>
                  )}
                </button>
              )}
            </div>

            <div style={{ marginTop: "10px" }}>
              <strong>Documents :</strong>
              <ul>
                {selectedClient.bilanDocument && (
                  <li>
                    <a
                      href={selectedClient.bilanDocument}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Consulter
                    </a>
                  </li>
                )}
                {selectedClient.identityDocument && (
                  <li>
                    <a
                      href={selectedClient.identityDocument}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Consulter
                    </a>
                  </li>
                )}
                {selectedClient.kbisDocument && (
                  <li>
                    <a
                      href={selectedClient.kbisDocument}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Consulter
                    </a>
                  </li>
                )}
                {selectedClient.statusDocument && (
                  <li>
                    <a
                      href={selectedClient.statusDocument}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Consulter
                    </a>
                  </li>
                )}
                {selectedClient.pieceIdentite && (
                  <li>
                    <a
                      href={selectedClient.pieceIdentite}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Document
                    </a>
                  </li>
                )}
                {!selectedClient.bilanDocument &&
                  !selectedClient.identityDocument &&
                  !selectedClient.kbisDocument &&
                  !selectedClient.statusDocument &&
                  !selectedClient.pieceIdentite && <li>Aucun document</li>}
              </ul>
            </div>

            {/* Section Commentaires */}
            <div className="bg-slate-50 rounded-lg border border-slate-200 p-6 mt-6">
              {/* Header */}
              <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#840040]" />
                Commentaires
                {commentaires.length > 0 && (
                  <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full text-white bg-[#840040]">
                    {commentaires.length}
                  </span>
                )}
              </h3>

              {/* Liste des commentaires */}
              <div className="space-y-3 max-h-[150px] overflow-y-auto pr-1 mb-4">
                {commentaires.length > 0 ? (
                  commentaires.map((c, index) => (
                    <div
                      key={index}
                      className={`bg-white rounded-lg p-3 border border-[#84004030] ${c._pending ? "opacity-60" : ""}`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0 bg-[#840040]">
                          {(c.nomComplet || "A")[0]}
                        </div>

                        <div className="flex-1">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-semibold text-slate-800 text-sm">
                              {c.nomComplet || "Anonyme"}
                            </span>

                            {c.createdAt && (
                              <span className="text-xs text-[#840040]">
                                {new Date(c.createdAt).toLocaleString("fr-FR")}
                              </span>
                            )}
                          </div>

                          <p className="text-sm text-slate-700 leading-relaxed">
                            {c.commentaire}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6 text-[#840040]">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucun commentaire pour le moment</p>
                  </div>
                )}

              </div>

              {/* Ajout commentaire */}
              <div className="border-t border-[#84004040] pt-4">
                <textarea
                  value={commentaireInput}
                  onChange={(e) => setCommentaireInput(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  className="w-full min-h-[80px] text-sm rounded-lg p-3 border border-[#84004040] focus:outline-none focus:ring-2 focus:ring-[#84004040]"
                />

                <div className="flex justify-end mt-3">
                  <button
                    onClick={handleAddComment}
                    disabled={isSendingComment}
                    className="text-white text-sm font-medium px-4 py-2 rounded-lg bg-[#840040] hover:opacity-90 transition disabled:opacity-50"
                  >
                    {isSendingComment ? "⏳ Envoi en cours..." : "Envoyer"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <br />
          <br />
        </div>
      </div>
    </div>
  );
};

export default AutresPopup;
